<template>
    <el-drawer :model-value="modelValue" class="dbconsole-blocks-drawer" direction="rtl" size="380px"
        :with-header="false" append-to-body @update:model-value="$emit('update:modelValue', $event)">
        <div class="dbconsole-tables">
            <div class="dbconsole-right__head">
                <span class="dbconsole-title">表</span>
                <span v-if="database" class="dbconsole-tables__db">{{ database }}</span>
                <span class="dbconsole-right__count">{{ tableFiltered.length }}</span>
                <el-button class="dbconsole-right__close" size="small" text @click="$emit('update:modelValue', false)">
                    <el-icon>
                        <Close />
                    </el-icon>
                </el-button>
            </div>
            <el-input v-model="tableKeyword" class="dbconsole-right__search" size="small" clearable
                placeholder="筛选表名（忽略大小写与下划线）">
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>

            <!-- 懒加载：根节点只渲染前 N 张表，滚到底部/点「加载更多」再追加下一批；表的字段在展开时才查 -->
            <div ref="tableListEl" class="dbconsole-tables__list" v-loading="tableLoading" @scroll="onTablesScroll">
                <el-empty v-if="tableLoaded && tableFiltered.length === 0" description="暂无表" :image-size="60" />
                <el-tree v-else ref="tableTree" :key="tablesTreeKey" class="dbconsole-tables__tree" :data="tableRows"
                    lazy :load="loadTableNode" :props="tableTreeProps" node-key="id" highlight-current>
                    <template #default="{ data }">
                        <span class="dbtree-node" :class="{ 'is-field': data.leaf }">
                            <span class="dbtree-node__label">{{ data.label }}</span>
                            <span v-if="data.type" class="dbtree-node__type">{{ data.type }}</span>
                            <span v-if="data.comment" class="dbtree-node__comment" :title="data.comment">{{ data.comment }}</span>
                        </span>
                    </template>
                </el-tree>
            </div>

            <div v-if="tableLoaded && tableFiltered.length > tableRows.length" class="dbconsole-tables__more">
                <span>已显示 {{ tableRows.length }} / 共 {{ tableFiltered.length }}</span>
                <el-button size="small" text type="primary" @click="loadMoreTables">加载更多</el-button>
            </div>
        </div>
    </el-drawer>
</template>

<script>
import request from '../../../../utils/request.js';

export default {
    name: 'DbTablesDrawer',
    props: {
        modelValue: { type: Boolean, default: false },
        connId: { type: [String, Number], default: '' },
        database: { type: String, default: '' }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            tableList: [],
            tableLoading: false,
            tableLoaded: false,
            tableKeyword: '',
            tablePage: 1,
            tablePageSize: 200,
            tablesTreeKey: 0,
            tableTreeProps: { label: 'label', children: 'children', isLeaf: 'leaf' }
        };
    },
    computed: {
        tableFiltered() {
            const kw = String(this.tableKeyword || '').toLowerCase().replace(/_/g, '');
            if (!kw) return this.tableList;
            return this.tableList.filter((t) => {
                const name = (t && typeof t === 'object') ? t.name : t;
                return String(name || '').toLowerCase().replace(/_/g, '').indexOf(kw) !== -1;
            });
        },
        tableRows() {
            const limit = this.tablePage * this.tablePageSize;
            return this.tableFiltered.slice(0, limit).map((t) => {
                const name = (t && typeof t === 'object') ? t.name : t;
                return {
                    id: 't:' + name,
                    label: name,
                    comment: (t && typeof t === 'object' && t.comment) ? t.comment : ''
                };
            });
        }
    },
    watch: {
        modelValue(open) {
            if (open) this.refresh();
        },
        tableKeyword() {
            this.tablePage = 1;
        }
    },
    methods: {
        refresh() {
            this.resetTablesTree();
            this.fetchTableList();
        },
        resetTablesTree() {
            this.tableList = [];
            this.tableLoaded = false;
            this.tableKeyword = '';
            this.tablePage = 1;
            this.tablesTreeKey += 1;
        },
        fetchTableList() {
            if (!this.connId) {
                this.tableList = [];
                this.tableLoaded = true;
                return Promise.resolve([]);
            }
            this.tableLoading = true;
            return request({
                url: '/dbManExec/tables',
                method: 'post',
                silent: true,
                data: { connId: this.connId, database: this.database }
            }).then(res => {
                const list = (res.meta && res.meta.tables) || [];
                this.tableList = Array.isArray(list) ? list : [];
                return this.tableList;
            }).catch(() => {
                this.tableList = [];
                return [];
            }).finally(() => {
                this.tableLoading = false;
                this.tableLoaded = true;
            });
        },
        loadMoreTables() {
            if (this.tableRows.length >= this.tableFiltered.length) return;
            const list = this.$refs.tableListEl;
            const keep = list ? list.scrollTop : 0;
            this.tablePage += 1;
            this.$nextTick(() => {
                if (list && list.scrollTop !== keep) list.scrollTop = keep;
            });
        },
        onTablesScroll(e) {
            const el = e.target;
            if (!el) return;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
                this.loadMoreTables();
            }
        },
        loadTableNode(node, resolve) {
            if (node.level === 0) {
                resolve([]);
                return;
            }
            const table = node.data.label;
            request({
                url: '/dbManExec/tableColumns',
                method: 'post',
                silent: true,
                data: { connId: this.connId, database: this.database, table }
            }).then(res => {
                const cols = (res.meta && res.meta.columns) || [];
                resolve(cols.map((c) => ({
                    id: 'f:' + table + '.' + c.name,
                    label: c.name,
                    type: c.type || '',
                    comment: c.comment || '',
                    leaf: true
                })));
            }).catch(() => resolve([]));
        }
    }
};
</script>

<style>
.dbconsole-blocks-drawer .el-drawer__body {
    padding: 0;
    overflow: hidden;
}

.dbconsole-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-right__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.dbconsole-right__count {
    padding: 1px 8px;
    border: 1px solid #d6e0f0;
    border-radius: 999px;
    background-color: #fff;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-tables__db {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #8a94a6;
    font-size: 12px;
}

.dbconsole-right__search {
    flex: 0 0 auto;
    margin-bottom: 8px;
}

.dbconsole-right__close {
    flex: 0 0 auto;
    margin-left: auto;
    padding: 0 4px;
}

.dbconsole-tables {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 12px 16px 18px;
    background-color: #fbfcfe;
    overflow: hidden;
}

.dbconsole-tables__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.dbconsole-tables__more {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #eef1f6;
    color: #8a94a6;
    font-size: 12px;
}

.dbconsole-tables__tree {
    background-color: transparent;
}

.dbtree-node {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    font-size: 13px;
}

.dbtree-node__label {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbtree-node__type {
    flex: 0 0 auto;
    color: #aab4c4;
    font-size: 12px;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.dbtree-node__comment {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #aab4c4;
    font-size: 12px;
}

.dbtree-node.is-field .dbtree-node__label {
    color: #5b6a80;
}
</style>
